import {Area} from "../../model/Area";
import * as rt from "@tanstack/react-table";
import * as bp from "@blueprintjs/core";
import React, {useCallback, useState} from "react";
import {AppTable, ColorCell, RowActions, SlicedCell} from "../../components/AppTable";
import {openGeoJson} from "../../utils/geojson";
import {ImageButton, ImageManager, ImageManagerProps} from "../../components/ImageManager";
import {AreasDialog, AreasDialogProps} from "./AreaDialog";
import AreaService from "../../service/AreaService";

export default function AreasTable({areas: data, refreshData: refreshAddresses}: { areas: Area[], refreshData: () => Promise<void> }) {
    const [dialogProps, setDialogProps] = useState<AreasDialogProps>({
        isOpen: false,
        close: async () => {
            setDialogProps({...dialogProps, isOpen: false});
            await refreshAddresses();
        },
        mode: 'new',
        area: {
            id: '',
            name: '',
            description: '',
            website: '',
            address: {
                streetAddress: '',
                postalCode: '',
                country: 'at'
            },
            color: '#ff0000',
            polygon: {},
            archived: false,
            previewImages: [],
            findings: 0
        }
    });
    const openCreateDialog = useCallback(() => setDialogProps((prev) => ({
        ...prev,
        isOpen: true,
        mode: 'new',
        area: {
            id: '',
            name: '',
            description: '',
            website: '',
            address: {
                streetAddress: '',
                postalCode: '',
                country: 'at'
            },
            color: '#ff0000',
            polygon: {},
            archived: false,
            previewImages: [],
            findings: 0
        }
    })), []);
    const openEditDialog = useCallback((area: Area) => setDialogProps((prev) => ({
        ...prev,
        isOpen: true,
        mode: 'edit',
        area
    })), []);
    const [imageManagerProps, setImageManagerProps] = useState<ImageManagerProps>({
        isOpen: false,
        onClose: async () => {
            setImageManagerProps({...imageManagerProps, isOpen: false});
            await refreshAddresses();
        },
        title: '',
        loadUrls: async () => [],
        addImage: async (_) => {},
        deleteImage: async (_) => {}
    });
    const columnHelper = rt.createColumnHelper<Area>();
    const handleDeleteArea = useCallback(async (area: Area) => {
        await AreaService.deleteArea(area);
        await refreshAddresses();
    }, [refreshAddresses]);

    const openImageManagerForArea = useCallback((area: Area) => {
        setImageManagerProps((prev) => ({
            ...prev,
            isOpen: true,
            loadUrls: async () => (await AreaService.getArea(area.id)).previewImages,
            addImage: async (file) => await AreaService.uploadImage(area, file),
            deleteImage: async (url) =>
                await AreaService.removeImage(area, url.substring(url.lastIndexOf('/') + 1))
        }));
    }, []);

    const columns = React.useMemo(() => [
        columnHelper.display({
            id: 'select',
            header: ({table}) => (
                <bp.Checkbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({row}) => (
                <bp.Checkbox
                    checked={row.getIsSelected()}
                    indeterminate={row.getIsSomeSelected?.()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('archived', {
            cell: info => <bp.Checkbox checked={!info.getValue()} disabled/>,
            header: () => <span>Aktiv</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('name', {
            cell: info => info.getValue(),
            header: () => <span>Name</span>
        }),
        columnHelper.accessor('description', {
            cell: info => <SlicedCell text={info.getValue()} max={100}/>,
            header: () => <span>Beschreibung</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('address', {
            cell: info => `${info.getValue().streetAddress}, ${info.getValue().postalCode}`,
            header: () => <span>Adresse</span>
        }),
        columnHelper.accessor('website', {
            cell: info =>
                <bp.AnchorButton icon="link"
                                 onClick={() => window.open(info.getValue(), '_blank')}
                                 disabled={info.getValue() === ''}
                                 outlined/>,
            header: () => <span>Website</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('color', {
            cell: info => <ColorCell color={info.getValue()}/>,
            header: () => <span>Farbe</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('polygon', {
            cell: info =>
                <bp.Button icon="map"
                           text="Öffnen"
                           onClick={() => openGeoJson(info.getValue())}/>,
            header: () => <span>Karte</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('findings', {
            cell: info => info.getValue(),
            header: () => <span>Aufzeichnungen</span>,
            enableColumnFilter: false
        }),
        columnHelper.accessor('previewImages', {
            cell: info =>
                <ImageButton imageUrls={info.getValue()}
                             openDialog={() => openImageManagerForArea(info.row.original)}/>,
            header: () => <span>Bilder</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.display({
            id: 'actions',
            cell: ({row: {original: area}}) =>
                <RowActions edit={() => openEditDialog(area)}
                            delete={() => handleDeleteArea(area)}/>,
            header: () => <span>Aktionen</span>
        })
    ], [openEditDialog, handleDeleteArea, openImageManagerForArea]);
    const [sorting, setSorting] = useState<rt.SortingState>([{id: 'name', desc: false}]);
    const [columnFilters, setColumnFilters] = useState<rt.ColumnFiltersState>([]);
    const [rowSelection, setRowSelection] = useState<rt.RowSelectionState>({});
    const table = rt.useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            rowSelection
        },
        getCoreRowModel: rt.getCoreRowModel(),
        getFilteredRowModel: rt.getFilteredRowModel(),
        getSortedRowModel: rt.getSortedRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        enableRowSelection: true,
        getRowId: (row) => row.id
    });
    const selectedRows = table.getSelectedRowModel().flatRows;
    const selectedCount = selectedRows.length;
    const handleBulk = async (archived: boolean) => {
        if (selectedCount === 0) return;
        const actionText = archived ? 'archivieren' : 'entarchivieren';
        if (!window.confirm(`Möchten Sie ${selectedCount} Einträge ${actionText}?`)) return;
        await Promise.all(selectedRows.map(r => AreaService.updateArea(r.original.id, {
            name: undefined,
            description: undefined,
            website: undefined,
            color: undefined,
            address: undefined,
            polygon: undefined,
            archived
        })));
        setRowSelection({});
        await refreshAddresses();
    };
    return <>
        <ImageManager {...imageManagerProps} />
        <AreasDialog {...dialogProps} />
        <AppTable table={table}
                  create={openCreateDialog}
                  selectedCount={selectedCount}
                  onBulkArchive={async () => handleBulk(true)}
                  onBulkUnarchive={async () => handleBulk(false)}
        />
    </>;
}